import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import DF from 'DisplayFramework';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DataGrid from 'components/DataGrid';
import { Col, notification, Popconfirm, Row, Form, InputNumber } from 'antd';
import {
  addParAssetUse,
  ConfigParAssetUseRequest,
  ConfigParAssetUseResponse,
  getParAssetUseByCriteria,
  GetParAssetUseByCriteriaParams,
  removeParAssetUse,
  updateParAssetUse,
} from 'services/pricing';
import { PlusOutlined } from '@ant-design/icons';
import DeleteButton from 'components/DeleteButton';
import { useForm } from 'antd/lib/form/Form';
import { isNil } from 'lodash';
import { indexBy, setValueByNamePath, shouldUpdateCompare } from 'utils/utils2';
import ParListSelect from 'components/ParListSelect';
import TaskInput from 'components/TaskInput';
import SaveButtonIcon from 'components/SaveBtn';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ModalControllerNV from 'components/ModalControllerNV';
import ParAssetConditionList from '../ParAsset/AssetUse/ParAssetConditionList';
import ButtonOperation from 'components/ButtonOperation';
import { useFormList } from 'hooks/useFormList';
import EditButton from 'components/EditButton';
import ProcedureInput from 'components/ProcedureInput';
import UsersList from 'components/UsersList';
import { AuthContext } from 'components/Auth/Auth';

type AssetUseListProps = {
  catalogid: number;
  asset: any;
  isModal?: string;
};
const AssetUseList = (props: AssetUseListProps) => {
  const { catalogid, asset, isModal } = props;
  const [form] = useForm();
  const fieldName = 'assetuse';
  const NEW_USE_ID = -1;
  const { isInspect } = useContext(AuthContext);
  const initialSearchParams: GetParAssetUseByCriteriaParams = {
    assetcategory: '' as any,
    assetfamily: '' as any,
    assettype: '' as any,
    useid: '' as any,
    attcode: '' as any,
    catalogid: catalogid ?? ('' as any),
    mgid: '' as any,
    offerid: '' as any,
    prcode: '' as any,
  };

  const [searchParams, setSearchParams] =
    useState<GetParAssetUseByCriteriaParams>(initialSearchParams);
  const {
    data: assetUse,
    refetch,
    isFetching,
  } = useQuery(
    ['getParAssetUseByCriteria', searchParams],
    () =>
      getParAssetUseByCriteria(searchParams).then((res) => {
        return res.list || [];
      }),
    { refetchOnMount: false, enabled: !isNil(catalogid) }
  );
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'useid',
    addAsFirst: true,
  });
  const [attcods, setAttcod] = useState('');
  useEffect(() => {
    if (assetUse) {
      form.setFieldsValue({
        [fieldName]: assetUse,
      });
    }
  }, [form, assetUse]);
  const getName = useCallback(
    (name: string) => (record: ConfigParAssetUseResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'useid')[record.useid];
      return [fieldName, index, name];
    },
    [form]
  );

  const onSaveOrEdit = async (values: ConfigParAssetUseRequest) => {
    const requestPaylod = {
      ...values,
    };
    // @ts-ignore
    if(requestPaylod.taskidtargetexceed === ''){
      requestPaylod.taskidtargetexceed = 0;
    }
    if (requestPaylod.useid === NEW_USE_ID) {
      addParAssetUse({ parassetUseReq: requestPaylod }).then((response) => {
        if (response.code === '200') {
          notification.success({ message: response.message });
          remove(-1);
          refetch();
        } else {
          notification.error({ message: response.message });
        }
      });
    } else {
      return updateParAssetUse({
        useid: Number(requestPaylod.useid),
        parassetUseReq: requestPaylod,
      }).then((response) => {
        if (response.code === '200') {
          notification.success({
            message: response.message,
          });
          form.setFieldsValue({
            [fieldName]: form
              .getFieldValue(fieldName)
              .map((use: any) =>
                use.useid === requestPaylod.useid
                  ? { ...use, isEdited: false }
                  : { ...use }
              ),
          });
          refetch();
        } else {
          notification.error({
            message: response.message,
          });
        }
      });
    }
  };

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'useid',
        width: 80,
      },
      {
        dataIndex: 'attcode',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('attcode')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect
                parcode="ASSETUSE"
                onSelect={(e) => {
                  setValueByNamePath(form, getName('useunittype')(record), '');
                  setValueByNamePath(
                    form,
                    getName('useunitperiod')(record),
                    ''
                  );
                  setAttcod(e);
                  setValueByNamePath(form, getName('attcode')(record), e);
                }}
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('attcodelabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'useunittype',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('useunittype')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect
                parcode="ATTUNIT"
                allowClear
                parvalueparent={attcods}
                onChange={(e) => {
                  setValueByNamePath(
                    form,
                    getName('useunitperiod')(record),
                    ''
                  );
                }}
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('useunittypelabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'useunitperiod',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('useunitperiod')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect parcode="PREPERIOD" allowClear />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('useunitperiodlabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },

      {
        dataIndex: 'minvalue',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item noStyle name={getName('minvalue')(record)}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('minvalue')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'maxvalue',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item noStyle name={getName('maxvalue')(record)}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('maxvalue')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'defaultvalue',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item noStyle name={getName('defaultvalue')(record)}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('defaultvalue')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'proccodeexceed',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('proccodeexceed')(record)}
              rules={[{ required: true }]}
            >
              <ProcedureInput entity="ASSET" />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('proccodeexceedlabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'taskidtargetexceed',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('taskidtargetexceed')(record)}
              rules={[{ required: true }]}
            >
              <TaskInput
                proccode={form.getFieldValue(getName('proccodeexceed')(record))}
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('taskidtargetexceedlabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'userequesttype',
        render: (value, record, index) => {
          return record?.useid === NEW_USE_ID || record?.isEdited ? (
            <Form.Item
              noStyle
              name={getName('userequesttype')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect
                onChange={() => {
                  //init all related fields
                  setValueByNamePath(
                    form,
                    getName('usereqreceivertype')(record),
                    ''
                  );
                  setValueByNamePath(
                    form,
                    getName('userrequestroleprofile')(record),
                    ''
                  );
                  setValueByNamePath(form, getName('userid')(record), null);
                  setValueByNamePath(
                    form,
                    getName('usereqreceivertypelabel')(record),
                    ''
                  );
                  setValueByNamePath(
                    form,
                    getName('userrequestroleprofilelabel')(record),
                    ''
                  );
                  setValueByNamePath(form, getName('username')(record), null);
                }}
                parcode="RATREQTYPE"
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('userequesttypelabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'usereqreceivertype',
        render: (value, record, index) => {
          return (record?.useid === NEW_USE_ID || record?.isEdited) &&
            record.userequesttype !== 'INFO' ? (
            <Form.Item
              noStyle
              name={getName('usereqreceivertype')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect
                onChange={() => {
                  setValueByNamePath(
                    form,
                    getName('userrequestroleprofile')(record),
                    ''
                  );
                  setValueByNamePath(
                    form,
                    getName('userrequestroleprofilelabel')(record),
                    ''
                  );
                  setValueByNamePath(form, getName('username')(record), null);

                  setValueByNamePath(form, getName('userid')(record), null);
                }}
                parcode="RECEIVTYPE"
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('usereqreceivertypelabel')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'userrequestroleprofile',
        render: (value, record, index) => {
          return (record?.useid === NEW_USE_ID || record?.isEdited) &&
            record.userequesttype !== 'INFO' &&
            (record.usereqreceivertype === 'UROLE' ||
              record.usereqreceivertype === 'USERJOB') ? (
            <Form.Item
              noStyle
              name={getName('userrequestroleprofile')(record)}
              rules={[{ required: true }]}
            >
              <ParListSelect
                parcode={
                  form.getFieldValue(getName('usereqreceivertype')(record)) ===
                  'UPROFILE'
                    ? 'USERJOB'
                    : form.getFieldValue(getName('usereqreceivertype')(record))
                }
              />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(
                getName('userrequestroleprofilelabel')(record)
              )}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'userid',
        render: (value, record, index) => {
          return (record?.useid === NEW_USE_ID || record?.isEdited) &&
            (record.usereqreceivertype === 'TARGETUSER' ||
              record.usereqreceivertype === 'USER') &&
            record.userequesttype !== 'INFO' ? (
            <Form.Item noStyle name={getName('userid')(record)}>
              <UsersList />
            </Form.Item>
          ) : (
            <Form.Item>
              {form.getFieldValue(getName('username')(record))}
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'usecondition',
        render: (value, record, index) => {
          return record?.useid !== NEW_USE_ID ? (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <ParAssetConditionList
                  onCancel={() => setVisible(false)}
                  showAsModal
                  visible={visible}
                  mgid={record.mgid}
                  useid={record.useid}
                />
              )}
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>
                  <GetLabel label="useCondition" />
                </a>
              )}
            />
          ) : null;
        },
        width: 150,
      },

      {
        dataIndex: 'Actions',
        width: '150px',
        fixed: 'right',
        render: (value, record, index) => (
          <Row>
            <Col span={12}>
              {!record.isEdited && record.useid !== NEW_USE_ID ? (
                <EditButton
                  onClick={() => {
                    form.setFieldsValue({
                      [fieldName]: form
                        .getFieldValue(fieldName)
                        .map((use: any) =>
                          use.useid === record.useid
                            ? { ...use, isEdited: true }
                            : { ...use }
                        ),
                    });
                  }}
                />
              ) : (
                <SaveButtonIcon
                  onClick={(ev) => {
                    onSaveOrEdit(record);
                  }}
                  onlyIcon
                />
              )}
            </Col>
            <Col span={12}>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  record.useid !== NEW_USE_ID
                    ? removeParAssetUse({
                        useid: record.useid,
                      }).then((res) => {
                        notification.success({ message: res.message });
                        refetch();
                      })
                    : remove(record.useid);
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </Col>
          </Row>
        ),
      },
    ],
    [searchParams, form, refetch, attcods, getName]
  );

  const addRow = () => {
    const row: any = {
      assetcategory: asset.assetcategory ?? ('' as any),
      assetfamily: asset.assetfamily ?? ('' as any),
      assettype: asset.assettype ?? ('' as any),
      attcode: '' as any,
      catalogid: catalogid,
      mgid: Number(localStorage.getItem('mgid')) ?? ('' as any),
      proccodeexceed: '' as any,
      taskidtargetexceed: '' as any,
      useid: NEW_USE_ID,
      usereqreceivertype: '' as any,
      userequesttype: '' as any,
      userid: Number(localStorage.getItem('userid')),
      userrequestroleprofile: '' as any,
      useunitperiod: '' as any,
      useunittype: '' as any,
      isEdited: false,
    };
    add(row);
  };

  return (
    <DF.Provider pagcode="assetuselist">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
          {({ getFieldValue }) => {
            const listUse: ConfigParAssetUseResponse[] =
              getFieldValue(fieldName);
            const isNewItemInTable =
              listUse?.find(({ useid }) => useid === NEW_USE_ID) !== undefined;
            return (
              <DataGrid
                titleComcode="ParAssetUseList"
                rowKey="useid"
                columns={columns}
                dataSource={listUse}
                isLoading={isFetching}
                hideFilters
                extraActions={
                  <ButtonOperation
                    comcode="Add"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => !isInspect && addRow()}
                    disabled={isNewItemInTable}
                    hidden={!!isModal}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default AssetUseList;
